import { useState, useMemo } from "react"
import {
  Plug,
  Search,
  Check,
  Clock,
  Zap,
  Mail,
  Video,
  FolderOpen,
  CreditCard,
  ArrowRight,
} from "lucide-react"
import { AnimatePresence } from "motion/react"
import { cn } from "@/utils/Functions"
import { notifyFeature, notifyDemoAction } from "@/utils/demoNotifications"
import { ChitBadge } from "@/components/ui/ChitBadge"
import { IntegrationCard } from "./components/IntegrationCard"
import { ManageConnectionsDialog } from "./components/ManageConnectionsDialog"
import { useIntegrationsData } from "@/hooks/integrations/useIntegrationsData"
import {
  useIntegrationConnections,
  useConnectIntegration,
  useDisconnectIntegration,
} from "@/hooks/integrations"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import type { IntegrationCategory } from "@/types/integrations.types"
import { Input } from "@/components/ui/Input"
import { Tabs, TabsList, TabsTab } from "@/components/ui/tabs"

const CORE_CONNECTIONS: {
  label: string
  description: string
  category: IntegrationCategory
  icon: typeof Mail
  accent: string
  accentBorder: string
}[] = [
  {
    label: "Email",
    description: "Send & receive deal emails",
    category: "Email",
    icon: Mail,
    accent: "bg-red-500/10",
    accentBorder: "border-red-500/20",
  },
  {
    label: "Meetings",
    description: "Record & transcribe calls",
    category: "Meetings",
    icon: Video,
    accent: "bg-violet-500/10",
    accentBorder: "border-violet-500/20",
  },
  {
    label: "Documents",
    description: "Store & manage deal files",
    category: "Storage",
    icon: FolderOpen,
    accent: "bg-yellow-500/10",
    accentBorder: "border-yellow-500/20",
  },
  {
    label: "Billing",
    description: "Track payments & invoices",
    category: "Billing",
    icon: CreditCard,
    accent: "bg-emerald-500/10",
    accentBorder: "border-emerald-500/20",
  },
]

type ActiveCategory = IntegrationCategory | "All"

function IntegrationsPage() {
  const isDemo = useIsDemo()

  // ── Both hooks fire; ternary selects data ────────────────────────────
  const demoData = useIntegrationsData()
  const {
    connectedIntegrationIds,
    integrationConnectionMap,
    getConnectionsForIntegration,
    refetch,
  } = useIntegrationConnections({ enabled: !isDemo })
  const { connect, connectingId } = useConnectIntegration()
  const { disconnect, disconnecting } = useDisconnectIntegration()

  // Demo uses mock data directly; prod enriches static list with live connections
  const CATEGORIES = demoData.categories
  const BASE_INTEGRATIONS = demoData.integrations

  const integrations = useMemo(() => {
    if (isDemo) return BASE_INTEGRATIONS
    return BASE_INTEGRATIONS.map((integration) => {
      const isConnected = connectedIntegrationIds.has(integration.id)
      return {
        ...integration,
        status: isConnected ? ("connected" as const) : integration.status,
      }
    })
  }, [isDemo, BASE_INTEGRATIONS, connectedIntegrationIds])

  // ── Local UI state ───────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState("")
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>("All")
  const [managingIntegrationId, setManagingIntegrationId] = useState<string | null>(null)

  // ── Derived data ─────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      connected: integrations.filter((i) => i.status === "connected").length,
      available: integrations.filter((i) => i.status === "available").length,
      comingSoon: integrations.filter((i) => i.status === "coming_soon").length,
    }),
    [integrations]
  )

  const connectedCategories = useMemo(
    () =>
      new Set(
        integrations
          .filter((i) => i.status === "connected")
          .map((i) => i.category)
      ),
    [integrations]
  )

  const filteredIntegrations = useMemo(() => {
    const q = searchQuery.toLowerCase()
    return integrations.filter((i) => {
      const matchesCategory =
        activeCategory === "All" || i.category === activeCategory
      const matchesSearch =
        !q ||
        i.name.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q) ||
        (i.provider && i.provider.toLowerCase().includes(q))
      return matchesCategory && matchesSearch
    })
  }, [searchQuery, activeCategory, integrations])

  // ── Handlers ─────────────────────────────────────────────────────────
  const handleConnect = async (id: string): Promise<void> => {
    if (isDemo) {
      const name = integrations.find((i) => i.id === id)?.name ?? id
      notifyDemoAction(`Connect ${name}`)
      return
    }

    const nylasIntegrations = [
      "gmail",
      "outlook-email",
      "google-calendar",
      "outlook-calendar",
    ]
    if (!nylasIntegrations.includes(id)) {
      console.log(`Integration ${id} not yet implemented`)
      return
    }

    const success = await connect(id)
    if (success) {
      refetch()
    }
  }

  const handleManage = (id: string) => {
    if (isDemo) {
      const name = integrations.find((i) => i.id === id)?.name ?? id
      notifyDemoAction(`Manage ${name}`)
      return
    }
    setManagingIntegrationId(id)
  }

  const handleNotify = (id: string) => {
    const name = integrations.find((i) => i.id === id)?.name ?? id
    if (isDemo) {
      notifyFeature(`Notify when ${name} is available`, true)
    } else {
      console.log(`Notify when ${name} is available`)
      // TODO: Implement notify feature
    }
  }

  const handleDisconnect = async (connectionId: string) => {
    const success = await disconnect(connectionId)
    if (success) {
      await refetch()
      if (managingIntegrationId) {
        const remainingConnections = getConnectionsForIntegration(managingIntegrationId)
        if (remainingConnections.length === 0) {
          setManagingIntegrationId(null)
        }
      }
    }
  }

  // Manage dialog data
  const managingIntegration = managingIntegrationId
    ? integrations.find((i) => i.id === managingIntegrationId) ?? null
    : null
  const managingConnections = managingIntegrationId
    ? getConnectionsForIntegration(managingIntegrationId)
    : []

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col bg-background">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <header className="shrink-0 border-b border-border bg-card/50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center size-10 rounded-md bg-muted">
                <Plug className="size-5 text-muted-foreground" />
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="font-semibold text-foreground tracking-tight">
                    Integrations
                  </h1>
                  <ChitBadge variant="success" size="sm">
                    {stats.connected} connected
                  </ChitBadge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Connect your favorite tools to streamline your workflow
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="px-6 pb-4">
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Check className="size-3.5 text-green-400" />
              <span className="text-muted-foreground">Connected</span>
              <span className="text-foreground font-medium">
                {stats.connected}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="size-3.5 text-blue-400" />
              <span className="text-muted-foreground">Available</span>
              <span className="text-foreground font-medium">
                {stats.available}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="size-3.5 text-muted-foreground/50" />
              <span className="text-muted-foreground">Coming Soon</span>
              <span className="text-foreground font-medium">
                {stats.comingSoon}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* ── Core Connections Banner ─────────────────────────────────── */}
      <div className="shrink-0 px-6 py-4 border-b border-border bg-muted/20">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Core Connections
          </p>
          <p className="text-xs text-muted-foreground">
            {CORE_CONNECTIONS.filter((c) => connectedCategories.has(c.category)).length} of {CORE_CONNECTIONS.length} connected
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {CORE_CONNECTIONS.map((item) => {
            const Icon = item.icon
            const isConnected = connectedCategories.has(item.category)
            return (
              <button
                key={item.label}
                onClick={() => {
                  setActiveCategory(item.category)
                  setSearchQuery("")
                }}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-xl border transition-colors text-left",
                  isConnected
                    ? "border-green-500/20 bg-green-500/5 hover:bg-green-500/10"
                    : "border-border bg-card hover:bg-muted/50"
                )}
              >
                <div
                  className={cn(
                    "shrink-0 size-9 rounded-lg flex items-center justify-center border",
                    isConnected
                      ? "bg-green-500/10 border-green-500/20"
                      : item.accent + " " + item.accentBorder
                  )}
                >
                  {isConnected ? (
                    <Check className="size-4 text-green-400" />
                  ) : (
                    <Icon className="size-4 text-foreground/60" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">
                      {item.label}
                    </p>
                    {!isConnected && (
                      <ArrowRight className="size-3 text-muted-foreground/50 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate">
                    {isConnected ? "Connected" : item.description}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* ── Toolbar ──────────────────────────────────────────────────── */}
      <div className="shrink-0 px-6 py-3 border-b border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        {/* Category pills */}
        <Tabs
          value={activeCategory}
          onValueChange={(v) => v && setActiveCategory(v as typeof activeCategory)}
        >
          <TabsList indicatorColor="secondary" className="overflow-x-auto no-scrollbar">
            {CATEGORIES.map((cat) => {
              const CatIcon = cat.icon
              return (
                <TabsTab key={cat.id} value={cat.id} className="text-xs whitespace-nowrap">
                  <CatIcon className="size-3.5" />
                  {cat.label}
                </TabsTab>
              )
            })}
          </TabsList>
        </Tabs>

        {/* Search */}
        <div className="relative flex-1 w-full sm:max-w-md">
          <Input
            startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
            type="search"
            placeholder="Search integrations..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            aria-label="Search integrations"
          />
        </div>
      </div>

      {/* ── Card grid ────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-auto p-6">
        {filteredIntegrations.length === 0 ? (
          <div className="h-full flex items-center justify-center py-12">
            <div className="text-center">
              <div className="size-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                <Search className="size-7 text-muted-foreground/40" />
              </div>
              <p className="text-foreground font-medium mb-1">
                No integrations found
              </p>
              <p className="text-muted-foreground text-sm">
                Try adjusting your search or category filter
              </p>
            </div>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredIntegrations.map((integration, idx) => (
                <IntegrationCard
                  key={integration.id}
                  integration={integration}
                  index={idx}
                  connection={isDemo ? undefined : integrationConnectionMap.get(integration.id)}
                  connecting={!isDemo && connectingId === integration.id}
                  onConnect={handleConnect}
                  onManage={handleManage}
                  onNotify={handleNotify}
                />
              ))}
            </div>
          </AnimatePresence>
        )}
      </div>

      {/* Manage Connections Dialog (prod only) */}
      {!isDemo && managingIntegration && (
        <ManageConnectionsDialog
          open={managingIntegrationId !== null}
          onOpenChange={(open) => !open && setManagingIntegrationId(null)}
          integration={managingIntegration}
          connections={managingConnections}
          onDisconnect={handleDisconnect}
          onAddAnother={async () => {
            await handleConnect(managingIntegration.id)
            await refetch()
          }}
          disconnecting={disconnecting}
          connecting={connectingId === managingIntegrationId}
        />
      )}
    </div>
  )
}

export default IntegrationsPage
